import type { BlogPost, Inquiry, Product } from './types';
import type { FieldIssue } from './formErrors';

const API_BASE = '/api';

/**
 * Error thrown by the API client when the server returns a non-2xx
 * response. Exposes the HTTP status and, when available, the structured
 * `issues` array from the server's zod validation so callers can render
 * per-field error decorations.
 */
export class ApiError extends Error {
  status: number;
  issues: FieldIssue[];
  constructor(message: string, status: number, issues: FieldIssue[] = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.issues = issues;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let detail = '';
    let issues: FieldIssue[] = [];
    try {
      const body = await res.json();
      detail = body?.error ?? body?.message ?? '';
      if (Array.isArray(body?.issues)) issues = body.issues as FieldIssue[];
    } catch {
      /* ignore */
    }
    throw new ApiError(detail || res.statusText, res.status, issues);
  }
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

export const api = {
  // Public
  listProducts: () => request<Product[]>('/products'),
  getProduct: (sku: string) => request<Product>(`/products/${encodeURIComponent(sku)}`),
  listBlogs: () => request<BlogPost[]>('/blogs'),
  getBlog: (slug: string) => request<BlogPost>(`/blogs/${encodeURIComponent(slug)}`),
  createInquiry: (payload: Partial<Inquiry> & { honeypot?: string }) =>
    request<{ id: string }>('/inquiries', { method: 'POST', body: JSON.stringify(payload) }),

  // Admin
  upsertProduct: (p: Partial<Product> & { rowKey: string; partitionKey: string }) =>
    request<Product>('/manage/products', { method: 'POST', body: JSON.stringify(p) }),
  deleteProduct: (category: string, sku: string) =>
    request<void>(`/manage/products/${encodeURIComponent(category)}/${encodeURIComponent(sku)}`, {
      method: 'DELETE',
    }),
  upsertBlog: (b: Partial<BlogPost> & { rowKey: string }) =>
    request<BlogPost>('/manage/blogs', { method: 'POST', body: JSON.stringify(b) }),
  deleteBlog: (slug: string) =>
    request<void>(`/manage/blogs/${encodeURIComponent(slug)}`, { method: 'DELETE' }),
  listInquiries: () => request<Inquiry[]>('/manage/inquiries'),
  updateInquiry: (partitionKey: string, rowKey: string, status: Inquiry['status']) =>
    request<Inquiry>(`/manage/inquiries/${encodeURIComponent(partitionKey)}/${encodeURIComponent(rowKey)}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  notifyIndex: () => request<{ submitted: number }>('/manage/notify-index', { method: 'POST' }),
  requestUploadSas: (
    container: 'product-images' | 'product-datasheets' | 'blog-images' | 'certificates',
    filename: string,
    contentType: string,
  ) =>
    request<{ uploadUrl: string; publicUrl: string }>('/manage/uploads', {
      method: 'POST',
      body: JSON.stringify({ container, filename, contentType }),
    }),
};

/**
 * Direct browser → blob upload using a short-lived SAS URL returned by the API.
 */
export async function uploadToBlob(
  uploadUrl: string,
  file: File | Blob,
  contentType: string,
): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': contentType,
    },
    body: file,
  });
  if (!res.ok) {
    throw new Error(`Blob upload failed: ${res.status} ${res.statusText}`);
  }
}
